import { httpsCallable, type HttpsCallableResult } from "firebase/functions";
import { mensajeDe, type CodigoError, type TipoEvento } from "@zociety/core";
import { codigoFirebase } from "./errorFirebase";
import { auth, functions } from "./firebase";

/**
 * Error de negocio ya traducido a un mensaje para la persona.
 *
 * El backend devuelve un código estable en `details.codigo`. La interfaz
 * decide el texto, así que cambiar la redacción no requiere redesplegar
 * funciones y las pruebas pueden afirmar sobre el código, no sobre el copy.
 */
export class ErrorApi extends Error {
  constructor(
    readonly codigo: CodigoError | "desconocido",
    mensaje: string,
    readonly campos?: string[]
  ) {
    super(mensaje);
    this.name = "ErrorApi";
  }
}

/*
 * Se identifica por la forma del error y no con `instanceof`.
 *
 * La comprobación por clase falla entre fragmentos del paquete: la clase que se
 * importa aquí no siempre es la que usó Firebase al lanzar el error. Pasó en la
 * pantalla de acceso, donde una contraseña equivocada acababa mostrando un
 * mensaje sobre la página en vez de sobre las credenciales.
 */
function traducir(e: unknown): ErrorApi {
  const codigo = codigoFirebase(e);
  if (codigo) {
    /*
     * Los detalles cuelgan del error, no de `customData`.
     *
     * Se buscaban solo en `customData.details`, que es donde los pone
     * **Authentication**. El SDK de Functions los pone en `details` directamente,
     * así que no se encontraban nunca y **todo error de negocio acababa como
     * `desconocido`**.
     *
     * No se notó porque el mensaje sí llegaba: la rama final usa `err.message`, y
     * el backend ya manda ahí el texto en español. La pantalla se veía bien y el
     * código estable —que es de lo que dependen las decisiones— se perdía.
     *
     * Lo que rompía de verdad: el reintento de `llamar()` compara contra
     * `sesion_caducada` para renovar el token tras un cambio de rol. Nunca se
     * disparaba, así que a quien acababan de ascender le tocaba cerrar sesión a
     * mano. Salió al revisar el embudo de la tienda en el navegador.
     *
     * Se conserva `customData` como respaldo: cuesta nada y cubre los errores de
     * Authentication, que sí lo usan.
     */
    const err = e as { details?: unknown; customData?: Record<string, unknown>; message?: string };
    const detalles = (err.details ?? err.customData?.["details"]) as
      | { codigo?: CodigoError; campos?: string[] }
      | undefined;
    if (detalles?.codigo) {
      return new ErrorApi(detalles.codigo, mensajeDe(detalles.codigo), detalles.campos);
    }
    if (codigo === "functions/unauthenticated") {
      return new ErrorApi("no_autenticado", "Tu sesión expiró. Inicia sesión de nuevo.");
    }
    if (codigo === "functions/deadline-exceeded" || codigo === "functions/unavailable") {
      return new ErrorApi("desconocido", "El servicio no responde. Revisa tu conexión e intenta de nuevo.");
    }
    return new ErrorApi("desconocido", err.message ?? "Ocurrió un error inesperado.");
  }
  return new ErrorApi("desconocido", "Ocurrió un error inesperado.");
}

function llamar<Entrada, Salida>(nombre: string) {
  const fn = httpsCallable<Entrada, Salida>(functions, nombre);

  return async (datos: Entrada, esReintento = false): Promise<Salida> => {
    try {
      const r: HttpsCallableResult<Salida> = await fn(datos);
      return r.data;
    } catch (e) {
      const error = traducir(e);

      /*
       * El backend rechaza los tokens emitidos antes de un cambio de rol. Es lo
       * que hace que una degradación surta efecto de inmediato, pero también
       * afecta a quien acaba de ser ascendido: su token todavía dice el rol
       * viejo. Renovarlo y reintentar una vez resuelve ese caso sin que la
       * persona tenga que cerrar sesión.
       *
       * Un solo reintento: si el token nuevo también se rechaza, el problema es
       * otro y hay que mostrarlo.
       */
      if (error.codigo === "sesion_caducada" && !esReintento && auth.currentUser) {
        await auth.currentUser.getIdToken(true);
        return llamar<Entrada, Salida>(nombre)(datos, true);
      }

      throw error;
    }
  };
}

/**
 * Crea el perfil del alumno si aún no existe. Es idempotente.
 *
 * El nombre se manda explícito porque al registrarse por correo el token de
 * sesión se emite antes de guardarlo y llegaría vacío. Con Google y Apple se
 * puede omitir: el proveedor lo entrega en el token desde el inicio.
 */
export const asegurarPerfil = llamar<
  { nombre?: string; apellido?: string },
  { creado: boolean }
>("asegurarPerfil");

// ── Correo ──────────────────────────────────────────────────────────────

/**
 * Manda o reenvía el correo de confirmación a quien tiene la sesión abierta.
 * El destinatario sale del token, no de la petición: nadie puede provocar
 * envíos a terceros.
 */
export const enviarVerificacion = llamar<
  Record<string, never>,
  { enviado: boolean; ya_verificado: boolean }
>("enviarVerificacion");

/**
 * Pide el correo para restablecer la contraseña.
 *
 * Responde igual exista la cuenta o no. Si distinguiera los dos casos, sería un
 * buscador de quién es alumna del estudio.
 */
export const enviarRestablecimiento = llamar<{ email: string }, { solicitado: boolean }>(
  "enviarRestablecimiento"
);

// ── Reservas ────────────────────────────────────────────────────────────
export const reservarClase = llamar<
  {
    clase_id: string;
    numero_lugar: number;
    con_invitado?: boolean;
    lugar_invitado?: number;
    datos_invitado?: { nombre: string; apellido: string; email: string; telefono?: string };
  },
  { reservacion_id: string; numero_lugar: number; consumio_credito: boolean; con_invitado: boolean }
>("reservarClase");

export const cancelarReserva = llamar<
  { reservacion_id: string },
  { early_cancel: boolean; reembolsado: boolean; invitacion_devuelta: boolean; horas_restantes: number }
>("cancelarReserva");

export const cambiarLugar = llamar<
  { reservacion_id: string; nuevo_lugar: number; nuevo_lugar_invitado?: number },
  { nuevo_lugar: number; nuevo_lugar_invitado: number | null }
>("cambiarLugar");

// ── Tienda ──────────────────────────────────────────────────────────────
export interface LineaCarritoApi {
  tipo: "paquete" | "merch";
  item_id: string;
  cantidad: number;
  bday_config?: { pago_modalidad: "50" | "100"; fecha: string; hora: string; coaches?: string[] };
}

export interface ResumenApi {
  lineas: {
    tipo: string;
    item_id: string;
    nombre: string;
    cantidad: number;
    precio_unitario_centavos: number;
    subtotal_centavos: number;
  }[];
  subtotal_centavos: number;
  descuento_centavos: number;
  total_centavos: number;
  creditos_a_acreditar: number;
  cupon_codigo: string | null;
  total_formateado?: string;
  descuento_formateado?: string;
}

export const previsualizarCarrito = llamar<
  { codigo: string; lineas: LineaCarritoApi[] },
  ResumenApi
>("previsualizarCarrito");

export type RespuestaCompra =
  | {
      requiere_pago: true;
      /**
       * Qué pasarela atendió el cobro.
       *
       * Decide si hay que pedir la tarjeta: con `stripe` falta ese paso y con
       * `sandbox` no. Lo dice el servidor en vez de deducirse de la forma del
       * secreto, para que cambiar de proveedor no dependa de un `startsWith`.
       */
      pasarela: string;
      intento: { id: string; estado: string; secreto_cliente: string | null; url_redireccion: string | null };
      resumen: ResumenApi;
    }
  | {
      requiere_pago: false;
      orden_id: string;
      total_centavos: number;
      total_formateado: string;
      creditos_acreditados: number;
    };

export const confirmarCompra = llamar<
  {
    lineas: LineaCarritoApi[];
    cupon_codigo?: string;
    metodo_entrega?: "digital" | "recoger_en_estudio" | "envio";
    payment_intent_id?: string;
  },
  RespuestaCompra
>("confirmarCompra");

// ── Medios de pago ──────────────────────────────────────────────────────

/**
 * Tarjeta guardada, tal como se le muestra a su dueña.
 *
 * Nunca llega el número completo: la clienta lo entrega directamente a la
 * pasarela y aquí solo viajan un identificador opaco y los últimos cuatro
 * dígitos, que es lo que hace falta para reconocerla.
 */
export interface MedioDePagoApi {
  id: string;
  marca: string;
  ultimos4: string;
  mes_expiracion: number;
  anio_expiracion: number;
}

export const mediosDePago = llamar<Record<string, never>, { medios: MedioDePagoApi[] }>(
  "mediosDePago"
);

export const borrarMedioDePago = llamar<{ medio_id: string }, { borrado: boolean }>(
  "borrarMedioDePago"
);

/** Abre el trámite para guardar una tarjeta sin comprar nada. */
export const prepararMedioDePago = llamar<
  Record<string, never>,
  { secreto_cliente: string; pasarela: string }
>("prepararMedioDePago");

/**
 * Permiso para que el formulario de pago muestre las tarjetas guardadas.
 * Devuelve `null` si esa persona todavía no tiene cliente en la pasarela.
 */
export const sesionDePago = llamar<Record<string, never>, { secreto_cliente: string | null }>(
  "sesionDePago"
);

// ── Diagnóstico ─────────────────────────────────────────────────────────

const enviarEvento = llamar<
  { tipo: TipoEvento; detalle?: string; contexto?: Record<string, unknown> },
  { registrado: boolean }
>("registrarEvento");

/**
 * Cuenta al servidor algo que solo el navegador vio.
 *
 * **Nunca lanza y nunca se espera.** Es la regla entera de esta función: se
 * llama en el camino de un error, y un diagnóstico que rompe o retrasa la
 * pantalla convierte un fallo en dos. Si no llega, no llegó.
 *
 * Existe porque una compra falló y en los registros del servidor todo salía
 * bien: el sistema deja de ver cuando la pasarela toma el control del navegador.
 */
export function reportar(
  tipo: TipoEvento,
  error?: unknown,
  contexto?: Record<string, unknown>
): void {
  const detalle =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === "string"
        ? error
        : error
          ? JSON.stringify(error)
          : "";

  void enviarEvento({ tipo, detalle, contexto }).catch(() => {
    /* Ni siquiera se registra en consola: si el servidor no responde, el usuario
       ya tiene un problema mayor y esto solo añadiría ruido. */
  });
}

// ── Carga masiva de horarios ────────────────────────────────────────────

/** Fila de la vista previa: una clase que la plantilla crearía o actualizaría. */
export interface ClasePrevia {
  dia: number;
  hora: string;
  disciplina: string;
  modalidad: string;
  coach: string;
  coach2?: string | null;
  nueva: boolean;
}

/** Celda que no se pudo interpretar, o que conviene mirar. */
export interface AvisoPlantilla {
  dia: number;
  hora: string;
  celda: string;
  motivo: string;
}

export const generarPlantillaHorarios = llamar<
  { hoja: string; lunes_ms: number },
  { nombre: string; archivo_base64: string }
>("generarPlantillaHorarios");

export const previsualizarPlantilla = llamar<
  { archivo_base64: string; lunes_ms: number },
  {
    hoja: string;
    crear: number;
    actualizar: number;
    clases: ClasePrevia[];
    errores: AvisoPlantilla[];
    avisos: AvisoPlantilla[];
    choques: { motivo: string }[];
  }
>("previsualizarPlantilla");

export const aplicarPlantillaHorarios = llamar<
  { archivo_base64: string; lunes_ms: number },
  { creadas: number; actualizadas: number; rechazadas: string[] }
>("aplicarPlantillaHorarios");

// ── Staff ───────────────────────────────────────────────────────────────
export const crearCuentaStaff = llamar<
  {
    email: string;
    password: string;
    rol: "coach" | "recepcion" | "admin";
    nombre: string;
    apellido?: string;
    especialidad?: string;
    biografia?: string;
    imagen?: string;
  },
  { uid: string; rol: string }
>("crearCuentaStaff");

export const crearAccesoPerfilExistente = llamar<
  { usuario_id: string; email: string; password: string },
  { uid: string; email: string; rol: string }
>("crearAccesoPerfilExistente");

/**
 * Dice cuáles de esos perfiles no tienen cuenta de acceso.
 *
 * Hay que preguntárselo al backend porque **Auth no se puede consultar desde el
 * cliente**. Antes se adivinaba por el largo del id del perfil, y esa adivinanza
 * no podía acertar para los perfiles a los que se les creó el acceso reutilizando
 * su id corto, que son justo los que la pantalla existe para resolver.
 */
export const staffSinAcceso = llamar<{ usuario_ids: string[] }, { sin_acceso: string[] }>(
  "staffSinAcceso"
);

/**
 * Cambia el rol y corta las sesiones abiertas de esa persona, de modo que el
 * cambio surte efecto de inmediato en todos sus dispositivos.
 */
export const cambiarRol = llamar<
  { usuario_id: string; rol: "alumno" | "coach" | "recepcion" | "admin"; motivo: string },
  { usuario_id: string; rol_anterior: string; rol_nuevo: string; sesiones_cortadas: boolean }
>("cambiarRol");

export const asignarPaquete = llamar<
  { usuario_id: string; paquete_id: string; cantidad?: number; motivo?: string },
  { compras: string[]; creditos_agregados: number; paquete: string }
>("asignarPaquete");

export const marcarAsistencia = llamar<
  { reservacion_id: string; asistencia: "pendiente" | "asistio" | "falta" },
  { reservacion_id: string; asistencia: string }
>("marcarAsistencia");

export const liquidarMulta = llamar<
  { multa_id: string; metodo: "terminal_fisica" | "efectivo" | "transferencia" | "en_linea"; referencia?: string },
  { multa_id: string; cuenta_reactivada: boolean; multas_pendientes: number }
>("liquidarMulta");

export const enviarNotificacion = llamar<
  {
    titulo: string;
    mensaje: string;
    link?: string;
    filtros?: Record<string, unknown>;
    target_emails?: string[];
  },
  { enviadas: number }
>("enviarNotificacion");
